import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentLayout } from '../../paymentLayout/PaymentLayout';
import { useAuth } from '../../../context/AuthContext';
import { TicketModal } from '../TicketModal/TicketModal';
import { QuickSignupModal } from '../QuickSignupModal/QuickSignupModal';
import { PhoneUpdateModal } from '../PhoneUpdateModal/PhoneUpdateModal';
import { RAZORPAY_KEY_ID } from '../../../config/razorpayConfig';
import './PayButton.css';

import { BASE_URL } from '../../../config/config';

const API_BASE_URL = BASE_URL;

const PayButton = ({
  userId: propUserId,
  eventId,
  ticketId,
  ticketsCount = 1,
  eventName = 'Event Ticket',
  prefill = {},
  initialPrice,
  onBookingSuccess,
  onBookingFailure,
  isSoldOutText,
  disabled = false,
  bookingId = null,
  orderId = null,
  platformfee,
  eventData = null,
  targetMonth = null,
  buttonLabel = 'Reserve'  // Pass event data for the ticket modal
}) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newUserId, setNewUserId] = useState(null);

  // Get the active user ID (from props, new signup, or auth context)
  const userId = newUserId || propUserId || user?.result?._id;

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Open Razorpay checkout directly
  const openRazorpayCheckout = async (orderInfo) => {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      alert('Razorpay SDK failed to load. Please check your connection.');
      return;
    }

    const options = {
      key: RAZORPAY_KEY_ID,
      amount: orderInfo.amount || initialPrice,
      currency: 'INR',
      name: 'Hobi App',
      description: eventName,
      order_id: orderInfo.orderId,
      handler: function (response) {
        console.log('Payment Success:', response);
        handlePaymentSuccess({
          ...response,
          bookingId: orderInfo.bookingId,
          transactionId: response.razorpay_payment_id,
        });
      },
      prefill: {
        name: prefill.name || '',
        email: prefill.email || '',
        contact: prefill.contact || '',
      },
      theme: {
        color: '#ff7e5f',
      },
      modal: {
        ondismiss: function () {
          console.log('Payment dismissed');
          handlePaymentFailure({ reason: 'dismissed', bookingId: orderInfo.bookingId });
        },
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', function (response) {
      console.log('Payment Failed:', response.error);
      handlePaymentFailure({
        ...response.error,
        bookingId: orderInfo.bookingId,
      });
    });
    razorpay.open();
  };

  // Create booking with a specific user ID
  const createBookingWithUserId = async (userIdToUse) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/bookings/create-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userIdToUse,
          eventId,
          ticketId,
          ticketsCount,
          targetMonth,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create booking');
      }

      const orderInfo = {
        bookingId: data.result._id,
        orderId: data.payment.id,
        amount: data.payment.amount,
      };
      
      setOrderData(orderInfo);
      setLoading(false);
      return orderInfo;
    } catch (error) {
      console.error('Booking creation failed:', error);
      alert(error.message || 'Failed to create booking. Please try again.');
      if (onBookingFailure) {
        onBookingFailure(error);
      }
      setLoading(false);
      return null;
    }
  };

  // Step 1: Call backend to create booking & get order_id
  const handleCreateBooking = async () => {
    // Check if authenticated OR we have a new user ID from signup
    if (!isAuthenticated && !newUserId) {
      setShowSignupModal(true);
      return null;
    }

    if (isAuthenticated && !user?.result?.phone && !newUserId) {
      setShowPhoneModal(true);
      return null;
    }
    
    return await createBookingWithUserId(userId);
  };

  // Step 2: Handle successful payment - update booking
  const handlePaymentSuccess = async (response) => {
    console.log('Payment successful:', response);
    try {
      const updateResponse = await fetch(`${API_BASE_URL}/bookings/confirm-booking`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: response.bookingId,
          transactionId: response.transactionId, // razorpay_payment_id
          platformfee : platformfee
        }),
      });
      const data = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(data.message || 'Failed to update booking');
      }

      // Create booking object for the ticket modal
      const bookingForModal = {
        _id: response.bookingId,
        ticketsCount: ticketsCount,
        amountPaid: initialPrice,
        eventId: eventData || { title: eventName }
      };
      
      setConfirmedBooking(bookingForModal);
      setShowTicketModal(true);
      
      if (onBookingSuccess) {
        onBookingSuccess(data);
      }
      setOrderData(null);
    } catch (error) {
      console.error('Booking update failed:', error);
      alert('Payment received but booking update failed. Please contact support.');
    }
  };

  const handlePaymentFailure = async (error) => {
    console.log('Payment failed:', error);
    // Cancel the pending booking if order was created
    if (error.bookingId) {
      try {
        await fetch(`${API_BASE_URL}/booking/cancel`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookingId: error.bookingId,
          }),
        });
      } catch (cancelError) {
        console.error('Failed to cancel booking:', cancelError);
      }
    }
    setOrderData(null);
    if (onBookingFailure) {
      onBookingFailure(error);
    }
  };

  // Handler for closing the ticket modal - navigate to profile
  const handleTicketModalClose = () => {
    setShowTicketModal(false);
    setConfirmedBooking(null);
    navigate('/profile');
  };

  // Handler for successful signup - auto-trigger payment
  const handleSignupSuccess = async (response) => {
    setShowSignupModal(false);
    
    // Get the new user ID from the signup response
    const signupUserId = response?.result?._id;
    if (signupUserId) {
      setNewUserId(signupUserId);
      
      // Directly create booking and open Razorpay
      const orderInfo = await createBookingWithUserId(signupUserId);
      if (orderInfo) {
        await openRazorpayCheckout(orderInfo);
      }
    }
  };

  // Handler for closing signup modal
  const handleSignupModalClose = () => {
    setShowSignupModal(false);
  };

  const handlePhoneUpdateSuccess = async () => {
    setShowPhoneModal(false);
    // After phone update, user state in context is already updated
    // Now proceed with booking
    const orderInfo = await createBookingWithUserId(userId);
    if (orderInfo) {
      await openRazorpayCheckout(orderInfo);
    }
  };

  const handlePhoneModalClose = () => {
    setShowPhoneModal(false);
  };

  return (
    <>
      <PaymentLayout
        name="Hobi App"
        description={eventName}
        prefill={prefill}
        onInitiate={bookingId ? null : handleCreateBooking}
        onSuccess={handlePaymentSuccess}
        onFailure={handlePaymentFailure}
        disabled={loading || disabled}
        bookingId={bookingId}
        orderId={orderId}
        amount={initialPrice}
      >
        {
          bookingId ? 
          <>Pending Payment</>
  :
   <a 
          href="/#" 
          className="button-3 w-button" 
          onClick={(e) => e.preventDefault()}
          style={{
            filter: (loading || disabled) ? 'grayscale(1)' : 'none',
            opacity: (loading || disabled) ? 0.7 : 1,
            pointerEvents: (loading || disabled) ? 'none' : 'auto',
            cursor: (loading || disabled) ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Processing...' : isSoldOutText || buttonLabel}
        </a>
        }
       
      </PaymentLayout>
      
      {/* Ticket Modal - shown after successful payment */}
      <TicketModal 
        isOpen={showTicketModal}
        onClose={handleTicketModalClose}
        booking={confirmedBooking}
      />

      {/* Quick Signup Modal - shown when unauthenticated user tries to book */}
      <QuickSignupModal
        isOpen={showSignupModal}
        onClose={handleSignupModalClose}
        onSuccess={handleSignupSuccess}
      />

      <PhoneUpdateModal 
        isOpen={showPhoneModal}
        onClose={handlePhoneModalClose}
        onSuccess={handlePhoneUpdateSuccess}
      />
    </>
  );
};

export default PayButton;